import React, { useState, useEffect } from 'react';
import { Modal } from 'antd';
import { getExtensionVersion, isExtensionEnvironment } from '../../utils/extensionUtils';
import languageManager from '../../utils/languageManager';

const ExtensionInstallGuide = ({ 
  visible, 
  onCancel, 
  onDownload, 
  extensionInfo 
}) => {
  const [isUpdate, setIsUpdate] = useState(false);
  const [currentVersion, setCurrentVersion] = useState('');

  useEffect(() => {
    if (visible && extensionInfo) {
      // 检查是否为更新
      const currentVer = getExtensionVersion();
      setCurrentVersion(currentVer);
      
      // 只有在扩展程序环境中且版本不同时才认为是更新
      const isExtensionEnv = isExtensionEnvironment();
      const isUpdateMode = isExtensionEnv && currentVer !== extensionInfo.version;
      setIsUpdate(isUpdateMode);
    }
  }, [visible, extensionInfo]);

  // 渲染更新指导内容
  const renderUpdateGuide = () => (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#1e293b' }}>
        🔄 {languageManager.t('extensionGuide.updateSteps')}
      </div>
      
      <div style={{ lineHeight: '1.6' }}>
        <div style={{ marginBottom: '8px' }}>
          <strong style={{ color: '#1890ff', fontSize: '13px' }}>1. {languageManager.t('extensionGuide.downloadNewVersion')}</strong>
          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
            {languageManager.t('extensionGuide.downloadZipToLocal')}
          </div>
        </div>
        
        <div style={{ marginBottom: '8px' }}>
          <strong style={{ color: '#1890ff', fontSize: '13px' }}>2. {languageManager.t('extensionGuide.extractAndReplace')}</strong>
          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
            {languageManager.t('extensionGuide.extractZipAndReplace')}
          </div>
        </div>
        
        <div style={{ marginBottom: '8px' }}>
          <strong style={{ color: '#1890ff', fontSize: '13px' }}>3. {languageManager.t('extensionGuide.refreshExtension')}</strong>
          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
            {languageManager.t('extensionGuide.clickRefreshButton')}
          </div>
        </div>
        
        <div>
          <strong style={{ color: '#1890ff', fontSize: '13px' }}>4. {languageManager.t('extensionGuide.verifyUpdate')}</strong>
          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
            {languageManager.t('extensionGuide.refreshNewTab')}
          </div>
        </div>
      </div>
    </div>
  );

  // 渲染安装指导内容
  const renderInstallGuide = () => (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#1e293b' }}>
        📋 {languageManager.t('extensionGuide.installSteps')}
      </div>
      
      <div style={{ lineHeight: '1.6' }}>
        <div style={{ marginBottom: '8px' }}>
          <strong style={{ color: '#1890ff', fontSize: '13px' }}>1. {languageManager.t('extensionGuide.downloadAndExtract')}</strong>
          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
            {languageManager.t('extensionGuide.downloadZipAndExtract')}
          </div>
        </div>
        
        <div style={{ marginBottom: '8px' }}>
          <strong style={{ color: '#1890ff', fontSize: '13px' }}>2. {languageManager.t('extensionGuide.openExtensionManager')}</strong>
          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
            {languageManager.t('extensionGuide.extensionUrls')}
          </div>
        </div>
        
        <div style={{ marginBottom: '8px' }}>
          <strong style={{ color: '#1890ff', fontSize: '13px' }}>3. {languageManager.t('extensionGuide.enableDeveloperMode')}</strong>
          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
            {languageManager.t('extensionGuide.turnOnDeveloperMode')}
          </div>
        </div>
        
        <div>
          <strong style={{ color: '#1890ff', fontSize: '13px' }}>4. {languageManager.t('extensionGuide.loadExtension')}</strong>
          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
            {languageManager.t('extensionGuide.clickLoadUnpacked')}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '20px' }}>🧩</span>
          <span>{isUpdate ? languageManager.t('extensionGuide.updateTitle') : languageManager.t('extensionGuide.installTitle')}</span>
        </div>
      }
      open={visible}
      onOk={onDownload}
      onCancel={onCancel}
      okText={languageManager.t('extensionGuide.downloadNow')}
      cancelText={languageManager.t('extensionGuide.installLater')}
      zIndex={2300}
      centered
      width={500}
      okButtonProps={{ 
        type: 'primary',
        style: { borderRadius: '8px', fontWeight: '600' }
      }}
      cancelButtonProps={{ 
        style: { borderRadius: '8px', fontWeight: '600' }
      }}
    >
      {extensionInfo && (
        <div style={{ padding: '12px 0' }}>
          {/* 扩展程序信息 */}
          <div style={{ 
            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
            borderRadius: '10px',
            padding: '12px',
            marginBottom: '16px',
            border: '1px solid #1890ff'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
              <div style={{ fontSize: '20px' }}>🧩</div>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#0c4a6e' }}>
                  {extensionInfo.versionName}
                </div>
                <div style={{ fontSize: '11px', color: '#0369a1' }}>
                  {languageManager.t('extensionGuide.version')} {extensionInfo.version} • {(extensionInfo.fileSize / 1024 / 1024).toFixed(2)} MB
                  {isUpdate && currentVersion && (
                    <span style={{ marginLeft: '8px', color: '#1890ff' }}>
                      ({languageManager.t('extensionGuide.currentVersion')}: v{currentVersion})
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div style={{ fontSize: '13px', color: '#0c4a6e', lineHeight: '1.4' }}>
              {isUpdate ? languageManager.t('extensionGuide.updateContent') + '：' : languageManager.t('extensionGuide.installContent') + '：'}{extensionInfo.updateNotes}
            </div>
            <div style={{ fontSize: '12px', color: '#0369a1', marginTop: '6px', fontStyle: 'italic' }}>
              {languageManager.t('extensionGuide.features')}
            </div>
          </div>

          {/* 根据是否为更新显示不同的指导 */}
          {isUpdate ? renderUpdateGuide() : renderInstallGuide()}

          {/* 更新时的特殊注意事项 */}
          {isUpdate && (
            <div style={{ 
              background: '#e6f7ff',
              border: '1px solid #1890ff',
              borderRadius: '6px',
              padding: '8px',
              marginBottom: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                <div style={{ fontSize: '14px', color: '#1890ff' }}>💡</div>
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#1890ff', marginBottom: '2px' }}>
                    {languageManager.t('extensionGuide.updateTips')}
                  </div>
                  <div style={{ fontSize: '11px', color: '#1890ff', lineHeight: '1.4' }}>
                    • {languageManager.t('extensionGuide.replaceEntireFolder')}<br/>
                    • {languageManager.t('extensionGuide.refreshAfterUpdate')}<br/>
                    • {languageManager.t('extensionGuide.backupBeforeUpdate')}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 注意事项 */}
          <div style={{ 
            background: '#e6f7ff',
            border: '1px solid #1890ff',
            borderRadius: '6px',
            padding: '8px',
            marginBottom: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
              <div style={{ fontSize: '14px', color: '#1890ff' }}>⚠️</div>
              <div>
                <div style={{ fontSize: '12px', fontWeight: '600', color: '#1890ff', marginBottom: '2px' }}>
                  {languageManager.t('extensionGuide.notes')}
                </div>
                <div style={{ fontSize: '11px', color: '#1890ff', lineHeight: '1.4' }}>
                  • {languageManager.t('extensionGuide.doNotDeleteFolder')}<br/>
                  • {languageManager.t('extensionGuide.chooseInstallAnyway')}<br/>
                  {isUpdate && `• ${languageManager.t('extensionGuide.closeTabsBeforeUpdate')}`}
                </div>
              </div>
            </div>
          </div>

          {/* 支持信息 */}
          <div style={{ 
            background: '#e6f7ff',
            border: '1px solid #1890ff',
            borderRadius: '6px',
            padding: '8px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ fontSize: '14px', color: '#1890ff' }}>💬</div>
              <div style={{ fontSize: '11px', color: '#1890ff' }}>
                {languageManager.t('extensionGuide.supportContact')}
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ExtensionInstallGuide; 